import React, { useState, useEffect } from 'react';
import { FaLongArrowAltDown, FaLongArrowAltUp } from 'react-icons/fa';
import { secondsToTime } from '../../../utils/time';
import makeScreenShot from '../../../utils/makeScreenShot';
import windows from '../../../png/windows.png';

import TaskDetails from './TaskDetails';
import type { ITask } from '../../../firebase/api/tasks-public';
import {
  deleteTask,
  updatePosition,
  updateStatus,
  updateTaskName,
} from '../../../firebase/api/tasks-public';
import {
  getTodayReport,
  createReport,
  recordScreen,
  createActivity,
} from '../../../firebase/api/tracking';
import {
  Arrows,
  InputWrapper,
  SButton,
  SInput,
  Sselect,
  TaskWrapper,
  SFaRegWindowClose,
  StartPause,
  Mask,
  ButtonWrapper,
} from './styled';
import isWeb from '../../../utils/isWeb';

const statuses = {
  todo: 'todo',
  done: 'done',
  inProgress: 'inProgress',
  notTracking: 'notTracking',
  onTestServer: 'onTestServer',
  readyToBuild: 'readyToBuild',
  finishUp: 'finishUp',
};

const TaskItem: React.FC<
  ITask & { self: ITask[] } & { index: number } & { canBeTracked: boolean } & {
    boardId: string;
  } & {
    trackingTaskId: string;
    setTrackingTaskId: (id: string) => void;
  }
> = (props) => {
  const [taskName, setTaskName] = useState(props.taskName);
  const [status, setStatus] = useState(props.status);
  const [isFocused, setIsFocused] = useState(false);
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const [detailBtnVisible, setDetailBtnVisible] = useState(false);
  const [secondsTracked, setSecondsTracked] = useState(0);
  const [isSync, setIsSync] = useState(false);

  const syncInterval = 30000;
  const screenshotInterval = 180000;

  // const syncInterval = 2000;
  // const screenshotInterval = 1000;

  const isTracking = props.trackingTaskId === String(props.id);

  useEffect(() => {
    void getTodayReport(String(props.id))
      .then((res) => {
        res && setSecondsTracked(res.seconds);
      })
      .catch(() => setSecondsTracked(0));
  }, [props.boardId, props.id]);

  useEffect(() => {
    isTracking &&
      setTimeout(() => {
        setSecondsTracked(secondsTracked + 1);
      }, 1000);
    if (!isTracking && !!secondsTracked) sync();
  }, [isTracking, secondsTracked]);

  const sync = () => {
    createReport(props.boardId, String(props.id), secondsTracked);
    createActivity(String(props.id), secondsTracked, props.boardId);
  };

  useEffect(() => {
    isSync && sync();
  }, [isSync]);

  let timeout: number;
  let screenShotTimeout: number;

  const turnOnSync = () => {
    setIsSync(true);
    setTimeout(() => {
      setIsSync(false);
    }, 1000);
  };

  const takeScreenshot = () => {
    makeScreenShot((screen) => recordScreen(String(props.id), screen));
  };

  useEffect(() => {
    clearTimeout(timeout);
    clearTimeout(screenShotTimeout);
  }, [props.boardId]);

  useEffect(() => {
    if (isTracking) {
      timeout = window.setInterval(() => turnOnSync(), syncInterval);
    } else {
      clearTimeout(timeout);
    }
    return () => clearTimeout(timeout);
  }, [isTracking]);

  useEffect(() => {
    if (isTracking) {
      screenShotTimeout = window.setInterval(() => takeScreenshot(), screenshotInterval);
    } else {
      clearTimeout(screenShotTimeout);
    }
    return () => clearTimeout(screenShotTimeout);
  }, [isTracking]);

  const nextItem = props.self[props.index + 1];
  const prevItem = props.self[props.index - 1];

  const getStatusColor = () => {
    switch (status) {
      case statuses.todo:
        return 'rgb(255, 100, 46)';
      case statuses.done:
        return 'rgb(0, 200, 117)';
      case statuses.inProgress:
        return 'rgb(255, 203, 0)';
      case statuses.onTestServer:
        return 'rgb(78, 204, 198)';
      case statuses.readyToBuild:
        return 'rgb(156, 211, 38)';
      case statuses.finishUp:
        return 'rgb(255, 21, 138)';
      default:
        return '#999';
    }
  };

  return (
    <>
      {isTracking && (
        <Mask>
          <span>{secondsToTime(secondsTracked)}</span>
        </Mask>
      )}
      <TaskWrapper isTracking={isTracking} isDetailsVisible={isDetailsVisible}>
        {isDetailsVisible && <div className="mask" onClick={() => setDetailsVisible(false)} />}
        <InputWrapper
          onMouseOver={() => setDetailBtnVisible(true)}
          onMouseLeave={() => setDetailBtnVisible(false)}
        >
          <SInput
            id={`task=${props.id}`}
            type="text"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              updateTaskName(props.boardId, props.id, taskName);
              setIsFocused(false);
            }}
          />
          {detailBtnVisible && (
            <>
              <Arrows>
                <FaLongArrowAltUp
                  onClick={() => {
                    if (!!prevItem?.id) {
                      updatePosition(props.boardId, props.id, props.rank - 1);
                      updatePosition(props.boardId, prevItem.id, props.rank);
                    }
                  }}
                />
                <FaLongArrowAltDown
                  onClick={() => {
                    if (!!nextItem?.id) {
                      updatePosition(props.boardId, props.id, props.rank + 1);
                      updatePosition(props.boardId, nextItem.id, props.rank);
                    }
                  }}
                />
              </Arrows>
            </>
          )}
          {detailBtnVisible && !isFocused && (
            <SButton onClick={() => setDetailsVisible(!isDetailsVisible)}>Детали</SButton>
          )}
          {isFocused && <SButton>Сохранить</SButton>}
        </InputWrapper>
        <Sselect
          onChange={(e) => {
            updateStatus(props.boardId, props.id, e.target.value);
            setStatus(e.target.value);
          }}
          value={status}
          background={getStatusColor()}
        >
          <option value={statuses.todo}>Сделать</option>
          <option value={statuses.inProgress}>В работе</option>
          {/* <option value={statuses.readyToBuild}>Ready to build</option>
        <option value={statuses.onTestServer}>На тестовом</option>
        <option value={statuses.finishUp}>Доделать</option> */}
          <option value={statuses.done}>Готово</option>
          {/* <option value={statuses.notTracking}>Не трекается</option> */}
        </Sselect>
        {/* {isWeb ? (
          <ButtonWrapper>
            <StartPause disabled={true}>Старт 00:00:00</StartPause>
            <div className="tooltip">
              Для трекинга времени{' '}
              <a href="/smart-tracker Setup 0.1.0.exe">
                скачайте программу <img src={windows} alt="windows" />
              </a>
            </div>
          </ButtonWrapper>
        ) : (
          <ButtonWrapper isMyBoard>
            <StartPause
              onClick={() => {
                if (isTracking) {
                  props.setTrackingTaskId('');
                } else {
                  props.setTrackingTaskId(String(props.id));
                  updateStatus(props.boardId, props.id, statuses.inProgress);
                  setStatus(statuses.inProgress);
                }
              }}
              disabled={!props.canBeTracked}
            >
              {isTracking
                ? `Пауза (${secondsToTime(secondsTracked)})`
                : `Старт (${secondsToTime(secondsTracked)})`}
            </StartPause>
            {!props.canBeTracked && (
              <div className="tooltip">
                Трекинг времени на этой доске доступен только для приглашенных Вами пользователей
              </div>
            )}
          </ButtonWrapper>
        )} */}
        {!props.trackingTaskId && (
          <SFaRegWindowClose onClick={() => deleteTask(props.boardId, props.id)} />
        )}
        {isDetailsVisible && (
          <TaskDetails
            boardId={props.boardId}
            task={{ ...props }}
            isDetailsVisible={isDetailsVisible}
            setDetailsVisible={setDetailsVisible}
          />
        )}
      </TaskWrapper>
    </>
  );
};

export default TaskItem;
